import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your privacy is our priority. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: December 15, 2024 | Last Updated: December 15, 2024</p>
        </div>

        <div className="space-y-8">
          {/* Quick Summary */}
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 text-blue-500 mr-3" />
              Privacy at a Glance
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Data Protection</h3>
                    <p className="text-gray-600 text-sm">Data is encrypted in transit (HTTPS); passwords are stored only as salted bcrypt hashes and are never visible to staff</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <UserCheck className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Your Control</h3>
                    <p className="text-gray-600 text-sm">You decide what information to share and with whom</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Lock className="w-5 h-5 text-purple-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">No Selling</h3>
                    <p className="text-gray-600 text-sm">We never sell your personal data to third parties</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Database className="w-5 h-5 text-orange-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Minimal Collection</h3>
                    <p className="text-gray-600 text-sm">We only collect data necessary for our service</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Information We Collect */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.1 Personal Information You Provide</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Registration Data:</strong> Name, email address, date of birth, gender, location</li>
                  <li>• <strong>Profile Information:</strong> Photos, bio, interests, profession, education, relationship preferences</li>
                  <li>• <strong>Communication Data:</strong> Messages, likes, swipes, matches, tip offers and messages</li>
                  <li>• <strong>Payment Information:</strong> Billing details for premium features (processed by secure third-party providers)</li>
                  <li>• <strong>Verification Data:</strong> Government-issued ID for age and identity verification</li>
                  <li>• <strong>Support Communications:</strong> Customer service inquiries and feedback</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.2 Information We Collect Automatically</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
                  <li>• <strong>Usage Data:</strong> App usage patterns, features accessed, time spent, session duration</li>
                  <li>• <strong>Location Data:</strong> Approximate location based on IP address or GPS (with consent)</li>
                  <li>• <strong>Technical Data:</strong> Log files, cookies, device identifiers, crash reports</li>
                  <li>• <strong>Analytics Data:</strong> User behavior patterns, feature usage statistics</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.3 Information from Third Parties</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Social Media:</strong> Profile information if you connect social media accounts</li>
                  <li>• <strong>Background Checks:</strong> Safety screening data from authorized providers</li>
                  <li>• <strong>Marketing Partners:</strong> Demographic and interest data for advertising</li>
                  <li>• <strong>Identity Verification:</strong> Age and identity confirmation from verification services</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* How We Use Information */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. How We Use Your Information</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Core Service Provision</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Create and maintain your profile and account</li>
                  <li>• Provide matching services and compatibility scoring</li>
                  <li>• Enable communication between matched users</li>
                  <li>• Process tipping transactions and facilitate user incentives</li>
                  <li>• Deliver premium features and subscription services</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Safety and Security</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Verify user identity and prevent underage usage (18+ requirement)</li>
                  <li>• Detect and prevent fraudulent accounts and inappropriate behavior</li>
                  <li>• Monitor for violations of Terms of Service and Community Guidelines</li>
                  <li>• Investigate reports of harassment, abuse, or other safety concerns</li>
                  <li>• Maintain platform security and prevent technical attacks</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.3 Communication and Marketing</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Send service-related notifications and account updates</li>
                  <li>• Provide customer support and respond to inquiries</li>
                  <li>• Send promotional materials and feature updates (with consent)</li>
                  <li>• Conduct surveys and collect feedback for service improvement</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.4 Legal Basis for Processing (GDPR)</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Contract Performance:</strong> Providing dating services per our Terms</li>
                  <li>• <strong>Legitimate Interest:</strong> Safety, security, fraud prevention, service improvement</li>
                  <li>• <strong>Consent:</strong> Marketing communications, location tracking, optional features</li>
                  <li>• <strong>Legal Obligation:</strong> Age verification, regulatory compliance, law enforcement requests</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Information Sharing */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. How We Share Your Information</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-yellow-800 mb-2">We DO NOT sell your personal data to third parties</h3>
              <p className="text-yellow-700 text-sm">Your personal information is never sold to advertisers, data brokers, or marketing companies.</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.1 With Other Users</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Profile information you choose to share (photos, bio, interests, etc.)</li>
                  <li>• Match compatibility scores and mutual connections</li>
                  <li>• Communication history with matched users</li>
                  <li>• Tip offers and messages (only to intended recipients)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.2 With Service Providers</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Payment Processors:</strong> Stripe, PayPal for subscription and tip processing</li>
                  <li>• <strong>Cloud Infrastructure:</strong> AWS, Google Cloud for hosting and storage</li>
                  <li>• <strong>Analytics:</strong> Google Analytics for usage statistics (anonymized)</li>
                  <li>• <strong>Customer Support:</strong> Zendesk for ticket management</li>
                  <li>• <strong>Identity Verification:</strong> Jumio, Onfido for age and ID verification</li>
                  <li>• <strong>Background Checks:</strong> Authorized screening providers for safety</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.3 Legal and Safety Requirements</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Law enforcement agencies when required by valid legal process</li>
                  <li>• Government authorities for regulatory compliance</li>
                  <li>• Legal proceedings involving safety threats or illegal activity</li>
                  <li>• Emergency situations to prevent harm to users or others</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.4 Business Transfers</h3>
                <p className="text-gray-700">In case of merger, acquisition, or sale of FlameLink, user data may be transferred to the new entity with advance notice to users and continued privacy protection.</p>
              </div>
            </div>
          </Card>

          {/* Data Retention */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Data Retention</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 Account Data</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Active Accounts:</strong> Data retained while account is active</li>
                  <li>• <strong>Deleted Accounts:</strong> When you delete your account (Settings → Delete my account) your profile, photos, swipes, matches, messages, notifications and AI analyses are removed immediately and the account record is anonymised. Moderation reports and administrative audit entries that reference your account ID are retained for safety and legal purposes.</li>
                  <li>• <strong>Legal Requirements:</strong> Some data retained longer for legal compliance (up to 7 years)</li>
                  <li>• <strong>Safety Records:</strong> Reports and investigations retained for safety purposes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Specific Data Types</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Messages:</strong> Retained until either user unmatches, blocks, or deletes their account</li>
                  <li>• <strong>Payment Data:</strong> Transaction records kept for 7 years (financial compliance)</li>
                  <li>• <strong>Verification Data:</strong> ID documents deleted after verification, records kept 3 years</li>
                  <li>• <strong>Analytics Data:</strong> Anonymized data may be retained indefinitely</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Your Rights */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Your Privacy Rights</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Universal Rights</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Access:</strong> Request copies of your personal data</li>
                  <li>• <strong>Correction:</strong> Update incorrect or incomplete information</li>
                  <li>• <strong>Deletion:</strong> Delete your account and data yourself at any time from Settings ("right to be forgotten")</li>
                  <li>• <strong>Portability:</strong> Download your data in machine-readable format</li>
                  <li>• <strong>Objection:</strong> Object to certain types of data processing</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2 Regional Rights</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>GDPR (EU):</strong> Restriction, objection, automated decision-making rights</li>
                  <li>• <strong>CCPA (California):</strong> Right to know, delete, opt-out of sale</li>
                  <li>• <strong>LGPD (Brazil):</strong> Confirmation, access, correction, deletion rights</li>
                  <li>• <strong>PIPEDA (Canada):</strong> Access, correction, complaint rights</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">How to Exercise Your Rights</h3>
              <p className="text-blue-700 text-sm mb-2">
                To exercise any of these rights, contact us at <strong>privacy@flamelink.com</strong> or use the privacy settings in your account.
              </p>
              <p className="text-blue-700 text-sm">
                We will respond to your request within 30 days (or as required by applicable law). Identity verification may be required.
              </p>
            </div>
          </Card>

          {/* International Transfers */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. International Data Transfers</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Global Service:</strong> FlameLink operates globally and may transfer your data to countries outside your jurisdiction for processing and storage.
              </p>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.1 Transfer Safeguards</h3>
                <ul className="space-y-2 ml-4">
                  <li>• <strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection laws</li>
                  <li>• <strong>Standard Contractual Clauses:</strong> EU-approved data transfer agreements</li>
                  <li>• <strong>Binding Corporate Rules:</strong> Internal data protection policies for multinational processing</li>
                  <li>• <strong>Certification Programs:</strong> Privacy Shield successor frameworks where available</li>
                </ul>
              </div>

              <p>
                <strong>US Operations:</strong> Primary data processing occurs in the United States with appropriate safeguards for international users.
              </p>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Privacy?</h3>
            <p className="text-gray-600 mb-6">Contact our privacy team for any questions or concerns</p>
            <Button
              onClick={() => window.open('mailto:privacy@flamelink.com', '_blank')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-8 py-3 rounded-full"
            >
              Contact Privacy Team
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;